import { useMemo } from 'react';

import type {
  UseWizardFooterConfigProps,
  WizardFooterConfig,
} from '@/components/wizards/types';

/**
 * Hook that returns footer configuration for Modal integration.
 * Returns config objects, NOT JSX (anti-pattern to memoize JSX).
 */
export function useWizardFooterConfig({
  canGoNext,
  canGoBack,
  isPending,
  isLastStep,
  onNext,
  onBack,
  onCancel,
  onSubmit,
  labels = {},
}: UseWizardFooterConfigProps): WizardFooterConfig {
  const {
    back = 'Back',
    cancel = 'Cancel',
    next = 'Next',
    submit = 'Create',
    submitting = 'Creating...',
  } = labels;

  return useMemo(
    () => ({
      hideCancelButton: true, // Always hide Modal's built-in cancel for wizards
      // Back button - only present when navigation back is possible
      backButton: canGoBack
        ? {
            label: back,
            onClick: onBack,
            disabled: isPending,
          }
        : undefined,
      // Cancel button - always present for right group
      cancelButton: {
        label: cancel,
        onClick: onCancel,
        disabled: isPending,
      },
      primaryButton: {
        text: isLastStep ? (isPending ? submitting : submit) : next,
        onClick: isLastStep ? onSubmit : onNext,
        disabled: !canGoNext || isPending,
        styleType: 'primary' as const,
        size: 'md' as const,
      },
    }),
    [
      canGoNext,
      canGoBack,
      isPending,
      isLastStep,
      onNext,
      onBack,
      onCancel,
      onSubmit,
      back,
      cancel,
      next,
      submit,
      submitting,
    ]
  );
}
